// src/pages/AdminTenantPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type ApiOk<T> = { ok: true; data: T };

type TenantDto = {
  id: string;
  name: string;
  inviteCode: string;
  createdAt: string | null;
  updatedAt: string | null;
};

type UserDto = {
  id: string;
  tenantId: string;
  username: string;
  role: string;
  mustChangePassword: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  failedLoginCount: number;
  lockedUntil: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

type TenantModuleDto = {
  tenantId: string;
  moduleKey:
    | "core"
    | "clients"
    | "projects"
    | "resources"
    | "subcontracts"
    | "expenses"
    | "payments"
    | "wages"
    | "partners"
    | "invoices";
  enabled: boolean;
};

const ALL_MODULES: Array<{
  key: TenantModuleDto["moduleKey"];
  label: string;
  desc: string;
}> = [
  { key: "core", label: "Core", desc: "Dashboard / core shell" },
  { key: "clients", label: "Clients", desc: "Customer list + contacts" },
  { key: "projects", label: "Projects", desc: "Active jobs + status" },
  { key: "resources", label: "Resources", desc: "Workers & subcontractors" },
  { key: "subcontracts", label: "Sub-Contracts", desc: "Agreements tied to projects" },
  { key: "expenses", label: "Expenses", desc: "General + subcontract payments" },
  { key: "payments", label: "Payments", desc: "Payments received" },
  { key: "wages", label: "Wages", desc: "Timesheets / pay workflow" },
  { key: "partners", label: "Partners", desc: "Partner disbursements" },
  { key: "invoices", label: "Invoices", desc: "Create invoices and track what’s billed/paid." },
];

async function api<T>(method: string, url: string, body?: any): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

function Badge({
  tone,
  children,
}: {
  tone: "good" | "warn" | "bad";
  children: React.ReactNode;
}) {
  const cls =
    tone === "good"
      ? "badge badgeGood"
      : tone === "warn"
      ? "badge badgeWarn"
      : "badge badgeBad";
  return <span className={cls}>{children}</span>;
}

function slugifyTenantId(input: string) {
  return (input || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminTenantPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"tenants" | "users">("tenants");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);

  // modules cache per tenantId
  const [modulesByTenant, setModulesByTenant] = useState<Record<string, TenantModuleDto[]>>({});
  const [modulesOpenTenantId, setModulesOpenTenantId] = useState<string | null>(null);

  // temp credential panel
  const [tempCreds, setTempCreds] = useState<null | { label: string; password: string }>(null);

  // Create Tenant form
  const [newTenantId, setNewTenantId] = useState("");
  const [newTenantName, setNewTenantName] = useState("");
  const [newTenantInvite, setNewTenantInvite] = useState("");

  // Create User form
  const [newUserTenantId, setNewUserTenantId] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newUserRole, setNewUserRole] = useState("tenant_user");
  const [newUserActive, setNewUserActive] = useState(true);

  const loadTenants = async () => {
    const resp = await api<ApiOk<TenantDto[]>>("GET", "/api/system/tenants");
    setTenants(resp.data || []);
  };

  const loadUsers = async () => {
    const resp = await api<ApiOk<UserDto[]>>("GET", "/api/system/users");
    setUsers(resp.data || []);
  };

  const refresh = async () => {
    setError(null);
    setBusy(true);
    try {
      await Promise.all([loadTenants(), loadUsers()]);
    } catch (e: any) {
      setError(e?.message || "Failed to load system admin data.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tenantRows = useMemo(
    () => tenants.slice().sort((a, b) => a.id.localeCompare(b.id)),
    [tenants]
  );

  const userRows = useMemo(
    () =>
      users
        .slice()
        .sort((a, b) =>
          (a.tenantId + "::" + a.username).localeCompare(b.tenantId + "::" + b.username)
        ),
    [users]
  );

  useEffect(() => {
    if (!newUserTenantId && tenantRows.length) {
      const firstNonSystem =
        tenantRows.find((t) => (t.id || "").toLowerCase() !== "system")?.id || tenantRows[0].id;
      setNewUserTenantId(firstNonSystem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantRows.length]);

  const patchTenant = async (tenantId: string, patch: { name?: string; inviteCode?: string }) => {
    setError(null);
    setBusy(true);
    try {
      const resp = await api<ApiOk<TenantDto>>(
        "PATCH",
        `/api/system/tenants/${encodeURIComponent(tenantId)}`,
        patch
      );
      const updated = resp.data;
      setTenants((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (e: any) {
      setError(e?.message || "Failed to update tenant.");
      await loadTenants().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const createTenant = async () => {
    const id = slugifyTenantId(newTenantId);
    const name = (newTenantName || "").trim();
    const inviteCode = (newTenantInvite || "").trim();

    if (!id) return setError(t("adminTenant.enterTenantId"));
    if (!name) return setError(t("adminTenant.enterTenantName"));
    if (id === "system") return setError(t("adminTenant.systemReserved"));

    setError(null);
    setBusy(true);
    try {
      await api<ApiOk<TenantDto>>("POST", "/api/system/tenants", {
        id,
        name,
        inviteCode: inviteCode || "",
      });

      await loadTenants();

      setNewTenantId("");
      setNewTenantName("");
      setNewTenantInvite("");
      setTab("tenants");
    } catch (e: any) {
      setError(e?.message || "Failed to create tenant.");
    } finally {
      setBusy(false);
    }
  };

  const deleteTenant = async (tenantId: string) => {
    const ok = window.confirm(t("adminTenant.confirmDeleteTenant", { id: tenantId }));
    if (!ok) return;

    setError(null);
    setBusy(true);
    try {
      await api<ApiOk<{ id: string }>>(
        "DELETE",
        `/api/system/tenants/${encodeURIComponent(tenantId)}`
      );
      setTenants((prev) => prev.filter((t) => t.id !== tenantId));
      setUsers((prev) => prev.filter((u) => u.tenantId !== tenantId));

      // cleanup modules cache
      setModulesByTenant((prev) => {
        const next = { ...prev };
        delete next[tenantId.toLowerCase()];
        return next;
      });
      if (modulesOpenTenantId?.toLowerCase() === tenantId.toLowerCase()) {
        setModulesOpenTenantId(null);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to delete tenant.");
    } finally {
      setBusy(false);
    }
  };

  const patchUser = async (userId: string, patch: { role?: string; isActive?: boolean }) => {
    setError(null);
    setBusy(true);
    try {
      const resp = await api<ApiOk<UserDto>>(
        "PATCH",
        `/api/system/users/${encodeURIComponent(userId)}`,
        patch
      );
      const updated = resp.data;
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (e: any) {
      setError(e?.message || "Failed to update user.");
      await loadUsers().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const createUser = async () => {
    const tenantId = (newUserTenantId || "").trim();
    const id = (newUserId || "").trim();
    const username = (newUsername || "").trim();

    if (!tenantId) return setError(t("adminTenant.selectTenant"));
    if (!id) return setError(t("adminTenant.enterUserId"));
    if (!username) return setError(t("adminTenant.enterUsername"));

    setError(null);
    setBusy(true);
    try {
      const resp = await api<
        ApiOk<{
          user: UserDto;
          tempPassword: string;
        }>
      >("POST", "/api/system/users", {
        tenantId,
        id,
        username,
        role: newUserRole,
        isActive: newUserActive,
      });

      setUsers((prev) => [...prev, resp.data.user]);

      setTempCreds({
        label: `Temporary password for ${resp.data.user.username} (${resp.data.user.id})`,
        password: resp.data.tempPassword,
      });

      setNewUserId("");
      setNewUsername("");
      setNewUserRole("tenant_user");
      setNewUserActive(true);
      setTab("users");
    } catch (e: any) {
      setError(e?.message || "Failed to create user.");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (userId: string) => {
    const ok = window.confirm(t("adminTenant.confirmResetPassword"));
    if (!ok) return;

    setError(null);
    setBusy(true);

    try {
      const resp = await api<any>(
        "POST",
        `/api/system/users/${encodeURIComponent(userId)}/reset_password`
      );

      const tempPassword = resp?.data?.tempPassword;
      if (!tempPassword) {
        throw new Error(resp?.error || "Reset password succeeded but no temp password was returned.");
      }

      setTempCreds({
        label: `Temporary password for ${userId}`,
        password: String(tempPassword),
      });

      await loadUsers();
    } catch (e: any) {
      setError(e?.message || "Failed to reset password.");
    } finally {
      setBusy(false);
    }
  };

  // ---- MODULES (NEW) ----

  const loadTenantModules = async (tenantId: string) => {
    const resp = await api<ApiOk<TenantModuleDto[]>>(
      "GET",
      `/api/system/tenants/${encodeURIComponent(tenantId)}/modules`
    );

    setModulesByTenant((prev) => ({
      ...prev,
      [tenantId.toLowerCase()]: resp.data || [],
    }));
  };

  const toggleTenantModule = async (tenantId: string, moduleKey: TenantModuleDto["moduleKey"], enabled: boolean) => {
    setError(null);
    setBusy(true);
    try {
      await api<ApiOk<TenantModuleDto>>(
        "PATCH",
        `/api/system/tenants/${encodeURIComponent(tenantId)}/modules`,
        { moduleKey, enabled }
      );

      await loadTenantModules(tenantId);
    } catch (e: any) {
      setError(e?.message || "Failed to update module.");
      await loadTenantModules(tenantId).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const openModules = async (tenantId: string) => {
    const nextId = tenantId;
    if (modulesOpenTenantId?.toLowerCase() === tenantId.toLowerCase()) {
      setModulesOpenTenantId(null);
      return;
    }

    setModulesOpenTenantId(nextId);
    const cacheKey = tenantId.toLowerCase();
    if (!modulesByTenant[cacheKey]) {
      setError(null);
      setBusy(true);
      try {
        await loadTenantModules(tenantId);
      } catch (e: any) {
        setError(e?.message || "Failed to load tenant modules.");
      } finally {
        setBusy(false);
      }
    }
  };

  const getEnabledMap = (tenantId: string) => {
    const rows = modulesByTenant[tenantId.toLowerCase()] || [];
    const map = new Map<string, boolean>();
    rows.forEach((r) => map.set(r.moduleKey, Boolean(r.enabled)));
    return map;
  };

  return (
    <div className="container" style={{ maxWidth: 1100 }}>
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ marginTop: 0 }}>{t("adminTenant.title")}</h2>
            <p style={{ color: "var(--muted)", marginTop: 6 }}>
              {t("adminTenant.subtitle")}
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className={`btn btnSm ${tab === "tenants" ? "" : "btnGhost"}`}
              onClick={() => setTab("tenants")}
              disabled={busy}
            >
              {t("adminTenant.tenantsBtn")}
            </button>
            <button
              type="button"
              className={`btn btnSm ${tab === "users" ? "" : "btnGhost"}`}
              onClick={() => setTab("users")}
              disabled={busy}
            >
              {t("adminTenant.usersBtn")}
            </button>
            <button type="button" className="btn btnSm" onClick={refresh} disabled={busy}>
              {busy ? t("adminTenant.refreshing") : t("adminTenant.refreshBtn")}
            </button>
          </div>
        </div>

        {error ? (
          <div className="pageNote" style={{ marginTop: 12, color: "var(--danger)" }}>
            {error}
          </div>
        ) : null}

        {tempCreds ? (
          <div className="card" style={{ marginTop: 12, border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 900 }}>{tempCreds.label}</div>
                <div className="pageNote" style={{ marginTop: 6 }}>
                  {t("adminTenant.tempPasswordNote")}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="btn btnSm btnGhost"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(tempCreds.password);
                    } catch {
                      // ignore
                    }
                  }}
                >
                  {t("adminTenant.copyBtn")}
                </button>
                <button type="button" className="btn btnSm" onClick={() => setTempCreds(null)}>
                  {t("adminTenant.closeBtn")}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="label">{t("adminTenant.tempPasswordLabel")}</label>
              <input className="input" value={tempCreds.password} readOnly />
            </div>
          </div>
        ) : null}

        {/* Create blocks */}
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <h3 style={{ marginTop: 0 }}>{t("adminTenant.createTenantTitle")}</h3>
              <div style={{ display: "grid", gap: 10 }}>
                <div>
                  <label className="label">{t("adminTenant.tenantIdLabel")}</label>
                  <input
                    className="input"
                    value={newTenantId}
                    onChange={(e) => setNewTenantId(e.target.value)}
                    placeholder={t("adminTenant.tenantIdPlaceholder")}
                    disabled={busy}
                  />
                  <div className="pageNote" style={{ marginTop: 6 }}>
                    {t("adminTenant.savedAs")} <b>{slugifyTenantId(newTenantId) || "—"}</b>
                  </div>
                </div>

                <div>
                  <label className="label">{t("adminTenant.tenantNameLabel")}</label>
                  <input
                    className="input"
                    value={newTenantName}
                    onChange={(e) => setNewTenantName(e.target.value)}
                    placeholder={t("adminTenant.tenantNamePlaceholder")}
                    disabled={busy}
                  />
                </div>

                <div>
                  <label className="label">{t("adminTenant.inviteCodeLabel")}</label>
                  <input
                    className="input"
                    value={newTenantInvite}
                    onChange={(e) => setNewTenantInvite(e.target.value)}
                    placeholder={t("adminTenant.inviteCodePlaceholder")}
                    disabled={busy}
                  />
                </div>

                <button type="button" className="btn btnSm" onClick={createTenant} disabled={busy}>
                  {busy ? t("adminTenant.working") : t("adminTenant.createTenantBtn")}
                </button>
              </div>
            </div>

            <div>
              <h3 style={{ marginTop: 0 }}>{t("adminTenant.createUserTitle")}</h3>
              <div style={{ display: "grid", gap: 10 }}>
                <div>
                  <label className="label">{t("adminTenant.tenantSelectLabel")}</label>
                  <select
                    className="input"
                    value={newUserTenantId}
                    onChange={(e) => setNewUserTenantId(e.target.value)}
                    disabled={busy}
                  >
                    {tenantRows.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                  <div>
                    <label className="label">{t("adminTenant.userIdLabel")}</label>
                    <input
                      className="input"
                      value={newUserId}
                      onChange={(e) => setNewUserId(e.target.value)}
                      placeholder={t("adminTenant.userIdPlaceholder")}
                      disabled={busy}
                    />
                  </div>
                  <div>
                    <label className="label">{t("adminTenant.usernameLabel")}</label>
                    <input
                      className="input"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder={t("adminTenant.usernamePlaceholder")}
                      disabled={busy}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                  <div>
                    <label className="label">{t("adminTenant.roleLabel")}</label>
                    <select
                      className="input"
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                      disabled={busy}
                    >
                      <option value="tenant_admin">tenant_admin</option>
                      <option value="tenant_user">tenant_user</option>
                      <option value="system_admin">system_admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">{t("adminTenant.statusLabel")}</label>
                    <select
                      className="input"
                      value={newUserActive ? "active" : "inactive"}
                      onChange={(e) => setNewUserActive(e.target.value === "active")}
                      disabled={busy}
                    >
                      <option value="active">{t("adminTenant.activeStatus")}</option>
                      <option value="inactive">{t("adminTenant.inactiveStatus")}</option>
                    </select>
                  </div>
                </div>

                <button type="button" className="btn btnSm" onClick={createUser} disabled={busy}>
                  {busy ? t("adminTenant.working") : t("adminTenant.createUserBtn")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {tab === "tenants" ? (
          <div className="card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>{t("adminTenant.tenantsTitle")}</h3>

            <div style={{ display: "grid", gap: 10 }}>
              {tenantRows.map((tenant) => {
                const isSystem = (tenant.id || "").toLowerCase() === "system";
                const isDemo = (tenant.id || "").toLowerCase() === "demo";
                const modulesOpen = modulesOpenTenantId?.toLowerCase() === tenant.id.toLowerCase();
                const enabledMap = getEnabledMap(tenant.id);

                return (
                  <div
                    key={tenant.id}
                    style={{
                      padding: 12,
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.65)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 900 }}>
                          {tenant.name}{" "}
                          <span style={{ color: "var(--muted)", fontWeight: 700 }}>({tenant.id})</span>
                        </div>
                        {isSystem ? <Badge tone="good">SYSTEM</Badge> : null}
                        {isDemo ? <Badge tone="warn">DEMO</Badge> : null}
                      </div>

                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <button
                          className="btn btnSm btnGhost"
                          type="button"
                          disabled={busy}
                          onClick={() => openModules(tenant.id)}
                        >
                          {modulesOpen ? t("adminTenant.hideModulesBtn") : t("adminTenant.manageModulesBtn")}
                        </button>

                        <button
                          className="btn btnSm btnDanger"
                          type="button"
                          disabled={busy || isSystem || isDemo}
                          title={
                            isSystem ? t("adminTenant.systemCantDelete") : isDemo ? t("adminTenant.demoCantDelete") : t("adminTenant.deleteTenantTitle")
                          }
                          onClick={() => deleteTenant(tenant.id)}
                        >
                          {t("adminTenant.deleteBtn")}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: 10, display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                      <div>
                        <label className="label">{t("adminTenant.tenantNameLabel")}</label>
                        <input
                          className="input"
                          value={tenant.name}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTenants((prev) => prev.map((x) => (x.id === tenant.id ? { ...x, name: v } : x)));
                          }}
                          onBlur={() => {
                            if (isSystem) return;
                            patchTenant(tenant.id, { name: tenant.name });
                          }}
                          disabled={busy || isSystem}
                        />
                      </div>

                      <div>
                        <label className="label">{t("adminTenant.inviteCodeLabel")}</label>
                        <input
                          className="input"
                          value={tenant.inviteCode ?? ""}
                          placeholder="e.g., NJDC or SYSTEM"
                          onChange={(e) => {
                            const v = e.target.value;
                            setTenants((prev) => prev.map((x) => (x.id === tenant.id ? { ...x, inviteCode: v } : x)));
                          }}
                          onBlur={() => {
                            if (isSystem) return;
                            patchTenant(tenant.id, { inviteCode: (tenant.inviteCode ?? "").trim() });
                          }}
                          disabled={busy || isSystem}
                        />
                        <div className="pageNote" style={{ marginTop: 6 }}>
                          {t("adminTenant.inviteCodeTip")}
                        </div>
                      </div>
                    </div>

                    {modulesOpen ? (
                      <div className="card" style={{ marginTop: 12, border: "1px solid var(--border)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{t("adminTenant.modulesForTitle", { tenant: tenant.id })}</div>
                            <div className="pageNote" style={{ marginTop: 6 }}>
                              {t("adminTenant.modulesNote")}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btnSm btnGhost"
                            disabled={busy}
                            onClick={() => loadTenantModules(tenant.id)}
                          >
                            {t("adminTenant.refreshModulesBtn")}
                          </button>
                        </div>

                        <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                          {ALL_MODULES.map((m) => {
                            const enabled = Boolean(enabledMap.get(m.key));
                            const disabledToggle = busy || (isSystem && m.key === "core"); // keep core always-on feel
                            return (
                              <div
                                key={m.key}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 10,
                                  alignItems: "center",
                                  padding: 10,
                                  border: "1px solid var(--border)",
                                  borderRadius: 12,
                                  background: "rgba(255,255,255,0.75)",
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 900 }}>
                                    {m.label}{" "}
                                    <span style={{ color: "var(--muted)", fontWeight: 700 }}>({m.key})</span>
                                  </div>
                                  <div className="pageNote" style={{ marginTop: 4 }}>{m.desc}</div>
                                </div>

                                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                  {enabled ? <Badge tone="good">{t("adminTenant.enabledBadge")}</Badge> : <Badge tone="bad">{t("adminTenant.disabledBadge")}</Badge>}
                                  <button
                                    type="button"
                                    className={`btn btnSm ${enabled ? "btnGhost" : ""}`}
                                    disabled={disabledToggle}
                                    onClick={() => toggleTenantModule(tenant.id, m.key, !enabled)}
                                    title={disabledToggle ? "Busy / protected" : "Toggle module"}
                                  >
                                    {enabled ? t("adminTenant.disableModuleBtn") : t("adminTenant.enableModuleBtn")}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    <div className="pageNote" style={{ marginTop: 10 }}>
                      {t("adminTenant.createdLabel")} {tenant.createdAt ?? "—"} • {t("adminTenant.updatedLabel")} {tenant.updatedAt ?? "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>{t("adminTenant.usersTitle")}</h3>

            <div style={{ display: "grid", gap: 10 }}>
              {userRows.map((u) => {
                const isSystemTenant = (u.tenantId || "").toLowerCase() === "system";
                return (
                  <div
                    key={u.id}
                    style={{
                      padding: 12,
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.65)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>
                          {u.username}{" "}
                          <span style={{ color: "var(--muted)", fontWeight: 700 }}>
                            ({u.tenantId}) • {u.id}
                          </span>
                        </div>
                        <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          {u.isActive ? <Badge tone="good">{t("adminTenant.activeStatus")}</Badge> : <Badge tone="bad">{t("adminTenant.inactiveStatus")}</Badge>}
                          {u.mustChangePassword ? <Badge tone="warn">{t("adminTenant.mustChangePassword")}</Badge> : null}
                          {isSystemTenant ? <Badge tone="good">{t("adminTenant.systemUser")}</Badge> : null}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <button
                          className="btn btnSm btnGhost"
                          type="button"
                          disabled={busy}
                          onClick={() => resetPassword(u.id)}
                        >
                          {t("adminTenant.resetPasswordBtn")}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: 10, display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr 1fr" }}>
                      <div>
                        <label className="label">{t("adminTenant.roleLabel")}</label>
                        <select
                          className="input"
                          value={u.role || "tenant_admin"}
                          disabled={busy}
                          onChange={(e) => {
                            const v = e.target.value;
                            setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role: v } : x)));
                          }}
                          onBlur={() => patchUser(u.id, { role: u.role })}
                        >
                          <option value="tenant_admin">tenant_admin</option>
                          <option value="tenant_user">tenant_user</option>
                          <option value="system_admin">system_admin</option>
                        </select>
                      </div>

                      <div>
                        <label className="label">{t("adminTenant.statusLabel")}</label>
                        <select
                          className="input"
                          value={u.isActive ? "active" : "inactive"}
                          disabled={busy}
                          onChange={(e) => {
                            const v = e.target.value === "active";
                            setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isActive: v } : x)));
                          }}
                          onBlur={() => patchUser(u.id, { isActive: u.isActive })}
                        >
                          <option value="active">{t("adminTenant.activeStatus")}</option>
                          <option value="inactive">{t("adminTenant.inactiveStatus")}</option>
                        </select>
                      </div>

                      <div>
                        <label className="label">{t("adminTenant.lastLoginLabel")}</label>
                        <input className="input" value={u.lastLoginAt ?? "—"} readOnly />
                      </div>
                    </div>

                    <div className="pageNote" style={{ marginTop: 10 }}>
                      {t("adminTenant.failedLoginsLabel")} {u.failedLoginCount ?? 0} • {t("adminTenant.lockedUntilLabel")} {u.lockedUntil ?? "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
